import { toast } from 'sonner';
import {
  createIndexedDbStorage,
  createOutbox,
  memoryStorage,
  newOperationId as mintOperationId,
  type DeadLetter,
  type FlushResult,
  type NewOutboxEntry,
  type OfflineStorage,
  type OutboxEntry,
  type OutboxTransport,
  type Submitted,
} from '@/lib/offline';
import i18n from '@/i18n';
import { GUEST_SESSION_KEY, api } from './api';

/**
 * This app's wiring for the shared offline outbox.
 *
 * The queue itself lives in `src/lib/offline`: the durable store, the
 * `operationId`, the flush lock, the 4xx policy (401/408/429 stay queued, the
 * rest dead-letter), the retry cap and the dead letter are one implementation
 * shared by every client, because several copies of the rule that decides
 * whether an order is lost meant several different answers. Only the host ports
 * — storage, transport, reachability — are supplied here, for the worst network
 * this product runs on: a captive portal, a basement, a phone changing APs.
 *
 * Two things here are guest-specific and are not in the staff hosts:
 *
 * 1. **A guest token cannot be refreshed.** A 401 means "scan the QR again",
 *    not "retry". The shared outbox already stops the flush on a 401 without
 *    spending an attempt, and `api.ts` sends the guest back to /scan; what this
 *    file adds is that the queue *survives* that round trip.
 * 2. **A queue belongs to a table.** See {@link align}.
 */

const NAMESPACE = 'venue.outbox';
/** The `qrToken` the queued work was taken at. See {@link align}. */
const OWNER_KEY = `${NAMESPACE}.table`;
/** Work stranded by a table change — kept, never flushed. See {@link quarantine}. */
const ABANDONED_KEY = `${NAMESPACE}.abandoned`;
/** Dead letters the guest has already been told about. See {@link announce}. */
const ANNOUNCED_KEY = `${NAMESPACE}.announced`;
/** Enough to explain what was dropped without letting a diagnostic list grow forever. */
const ABANDONED_LIMIT = 50;
/**
 * On venue Wi-Fi `navigator.onLine` stays `true` behind a captive portal and no
 * `online` event ever fires, so a timer — not an event — is the real recovery
 * path. A tick on an empty queue is one indexed read.
 */
const RETRY_INTERVAL_MS = 15_000;

// ---------------------------------------------------------------- storage

let durable = true;

/**
 * IndexedDB, from the one adapter in `src/lib/offline`. It rejects rather than
 * degrading — private browsing and a zero quota both hand back a database whose
 * first write fails — so the fallback is chosen here, explicitly and loudly.
 *
 * The fallback is in-memory and **not** durable: it survives a dropped packet,
 * which is what this file exists to fix, but not a reload. {@link isDurable}
 * lets a caller find out; there is nothing better in a browser (`localStorage`
 * is synchronous, ~5MB and non-transactional — see the adapter's own note).
 */
async function openStorage(): Promise<OfflineStorage> {
  try {
    return await createIndexedDbStorage({ namespace: NAMESPACE });
  } catch (error) {
    durable = false;
    console.warn(
      '[outbox] IndexedDB is unavailable, so the guest queue cannot survive a reload',
      error,
    );
    return memoryStorage();
  }
}

let backend: Promise<OfflineStorage> | null = null;
const resolveStorage = (): Promise<OfflineStorage> => (backend ??= openStorage());

/**
 * The port, resolved lazily. Opening a database is async and the outbox is
 * built at import time; every method here is already async, so the wait costs a
 * microtask and no caller needs to know.
 */
const storage: OfflineStorage = {
  getItem: async (key) => (await resolveStorage()).getItem(key),
  setItem: async (key, value) => (await resolveStorage()).setItem(key, value),
  removeItem: async (key) => (await resolveStorage()).removeItem(key),
};

/** False once storage has been resolved to the in-memory fallback. */
export const isDurable = (): boolean => durable;

// ------------------------------------------------------------ table identity

/**
 * The table this device is sitting at, or `null` when no session is open.
 *
 * Read from the persisted session rather than from Redux so the timer and the
 * `online` listener — neither of which has a store — get the same answer, and
 * so this module cannot form a cycle with the slice that writes it.
 *
 * `qrToken` is the identity: the code printed on the table, stable across a
 * reload, a re-scan and a PIN retry, and different between two tables. A
 * session persisted before that field existed falls back to its session id —
 * weaker (a re-scan mints a new one) but never *equal* for two different
 * tables, which is the only property that must hold.
 */
export function currentTableKey(): string | null {
  try {
    const raw = localStorage.getItem(GUEST_SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as { qrToken?: unknown; sessionId?: unknown };
    if (typeof session?.qrToken === 'string' && session.qrToken) return session.qrToken;
    if (typeof session?.sessionId === 'string' && session.sessionId) {
      return `session:${session.sessionId}`;
    }
    return null;
  } catch {
    return null;
  }
}

/** Thrown by the transport when the table changed underneath an in-flight run. */
class StaleTableError extends Error {
  constructor() {
    super('The queued work belongs to a different table');
    this.name = 'StaleTableError';
  }
}

// --------------------------------------------------------------- transport

/**
 * The guard that keeps one guest's order off another guest's bill.
 *
 * Every guest route is bound to the session in the token, so a request queued
 * at table 4 and sent while table 12's token is in storage does not fail — it
 * *succeeds, against table 12*. {@link align} prevents that before a flush
 * starts; this closes the remaining window, where the guest scans a new table
 * mid-flush. Rejecting with no `response` makes it a transport failure, so the
 * entry stays queued and nothing is discarded.
 */
const transport: OutboxTransport = async (request) => {
  const table = currentTableKey();
  const owner = await storage.getItem(OWNER_KEY);
  if (!table || owner !== table) throw new StaleTableError();
  return api.request(request);
};

/**
 * Trustworthy in one direction only: `false` means there is no link at all.
 * `true` routinely means "associated with an AP that answers nothing", so it is
 * treated as "worth trying" and the request decides — reading it the other way
 * costs the queue its chance to drain.
 */
const isReachable = async (): Promise<boolean> =>
  typeof navigator === 'undefined' || navigator.onLine !== false;

/** Monotonic-ish, collision-resistant enough for a per-device queue. */
export const newOperationId = (): string => mintOperationId();

export const outbox = createOutbox({
  storage,
  transport,
  isReachable,
  namespace: NAMESPACE,
  newOperationId: () => newOperationId(),
});

// ------------------------------------------------------------- the boundary

export interface AbandonedWrite {
  entry: OutboxEntry;
  /** The table key the work was taken at. */
  table: string;
  abandonedAt: number;
}

async function readJson<T>(key: string, fallback: T): Promise<T> {
  try {
    const raw = await storage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Move everything queued for a previous table out of the live queue.
 *
 * `guestSlice` already discards the cart on this boundary, for the reason
 * spelled out there. Unsent writes are the same argument one step later — and
 * worse, because they carry no price to look wrong, they would simply land on
 * whoever is sitting here now.
 *
 * They are not deleted. The outbox invariant is delivered, queued or visible —
 * never nowhere — so they are parked in a store the flush loop never reads. Not
 * the dead letter: retrying one of those puts it back in the live queue, which is
 * precisely the delivery that must not happen.
 */
async function quarantine(owner: string): Promise<number> {
  const [queue, letters] = await Promise.all([outbox.all(), outbox.dead()]);
  const stranded = [...queue, ...letters.map((letter) => letter.entry)];

  if (stranded.length) {
    const kept = await readJson<AbandonedWrite[]>(ABANDONED_KEY, []);
    const known = new Set(kept.map((row) => row.entry.operationId));
    const merged = [
      ...kept,
      ...stranded
        .filter((entry) => !known.has(entry.operationId))
        .map((entry) => ({ entry, table: owner, abandonedAt: Date.now() })),
    ];
    // Written *before* the queue is cleared: a crash in between costs one
    // duplicate diagnostic row, never the record of what the guest asked for.
    await storage.setItem(ABANDONED_KEY, JSON.stringify(merged.slice(-ABANDONED_LIMIT)));
  }

  await outbox.clear();
  await storage.removeItem(ANNOUNCED_KEY);
  return stranded.length;
}

/**
 * Settle which table the queue belongs to, and answer where it may be sent.
 *
 * - **No open session** → `null`. Hold everything exactly as it is and send
 *   nothing. This is the path a 401 takes: a guest token cannot be refreshed,
 *   so the credential is thrown away and the guest is sent to /scan. Re-opening
 *   the *same* table restores the owner match and the queue drains — which is
 *   the whole reason `guestSlice` persists `qrToken`.
 * - **Same table** → flush normally.
 * - **A different table** → the previous table's work is quarantined before a
 *   single request can leave, and the guest is told, exactly as they are told
 *   about the basket.
 */
async function align(): Promise<string | null> {
  const table = currentTableKey();
  if (!table) return null;

  const owner = await storage.getItem(OWNER_KEY);
  if (owner === table) return table;

  if (owner !== null) {
    const stranded = await quarantine(owner);
    if (stranded) toast.warning(i18n.t('guest.sync.tableChanged', { count: stranded }));
  }
  await storage.setItem(OWNER_KEY, table);
  return table;
}

/**
 * One promise chain for the boundary check, for the same reason the outbox has
 * one for storage: a tap, a timer tick and an `online` event can all reach it at
 * once, and two interleaved quarantines would each read the queue the other is
 * clearing.
 */
let chain: Promise<unknown> = Promise.resolve();

function serialised<T>(work: () => Promise<T>): Promise<T> {
  const run = chain.then(work, work);
  chain = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

// -------------------------------------------------------------- rejections

/**
 * An error shaped like an axios rejection, so the screens' existing
 * `errorMessage(error, fallback)` reads the server's own words out of it.
 */
function apiShapedError(message: string, status: number | null = null): Error {
  return Object.assign(new Error(message), { response: { status, data: { message } } });
}

/** `service:CALL_WAITER` → the noun a guest would use for it. */
function describe(entry: OutboxEntry): string {
  const kind = (entry.label ?? '').split(':')[0];
  const known = ['order', 'service', 'feedback'];
  return i18n.t(`guest.sync.item.${known.includes(kind) ? kind : 'other'}`);
}

/**
 * Tell the guest about work the server refused while nobody was looking at it.
 *
 * The dead letter exists so a rejection is never silent, and every staff app has
 * a banner where a person retries or discards it. A guest has no such surface
 * and no second attempt worth offering, so the refusal is spoken once, in the
 * server's words, rather than left in a store nothing renders. The entry stays
 * on disk, and the ids already announced are kept so a reload does not repeat it.
 */
async function announce(): Promise<void> {
  const letters = await outbox.dead();
  const announced = await readJson<string[]>(ANNOUNCED_KEY, []);
  const seen = new Set(announced);
  const fresh = letters.filter((letter) => !seen.has(letter.entry.operationId));

  for (const letter of fresh) {
    toast.error(
      i18n.t('guest.sync.rejected', { item: describe(letter.entry), message: letter.message }),
    );
  }

  // Rewritten from the live dead letter, so the list shrinks with it.
  if (fresh.length || announced.length !== letters.length) {
    await storage.setItem(
      ANNOUNCED_KEY,
      JSON.stringify(letters.map((letter) => letter.entry.operationId)),
    );
  }
}

// ------------------------------------------------------------------ the API

/** What a caller hands {@link submitGuestWrite}; the queue stamps the rest. */
export type GuestWrite = Omit<NewOutboxEntry, 'operationId'> &
  Partial<Pick<OutboxEntry, 'operationId'>>;

/**
 * Queue one guest mutation durably, then drain the queue in order.
 *
 * The write is on disk before the network is touched, so the guest never loses
 * it; `synced` says whether it actually reached the server, and `false` means
 * "waiting", never "lost".
 *
 * A rejection is thrown rather than parked: the guest is looking at the result
 * of this tap right now, and the reason they cannot have it is worth more than
 * a dead letter this app has nowhere to render — the same trade the shared
 * outbox makes in `mutate()`.
 */
export async function submitGuestWrite<T>(entry: GuestWrite): Promise<Submitted<T>> {
  const table = await serialised(align);
  // Nothing may be queued without a table to queue it against: an entry with no
  // owner could not be told apart from a previous table's work later on.
  if (!table) throw apiShapedError(i18n.t('guest.sync.noSession'), 401);

  const submitted = await outbox.submit<T>(entry);

  const mine = submitted.synced
    ? undefined
    : (await outbox.dead()).find((letter) => letter.entry.operationId === submitted.operationId);
  if (mine) await outbox.discardDead(submitted.operationId);

  // The same flush may have carried older entries the server also refused.
  await announce();

  if (mine) throw apiShapedError(mine.message, mine.status);
  return submitted;
}

/** Drain whatever is waiting, if it belongs to the table we are sitting at. */
export async function flushGuestQueue(): Promise<FlushResult | null> {
  const table = await serialised(align);
  if (!table) return null;
  const result = await outbox.flush();
  await announce();
  return result;
}

/** How many writes are still waiting — for a banner, and for the tests. */
export const pendingCount = async (): Promise<number> => (await outbox.all()).length;

/** Refused work still on disk. */
export const deadLetters = (): Promise<DeadLetter[]> => outbox.dead();

/** Writes stranded by a table change; they are never sent from here. */
export const abandonedWrites = (): Promise<AbandonedWrite[]> =>
  readJson<AbandonedWrite[]>(ABANDONED_KEY, []);

// ------------------------------------------------------------------ triggers

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => void flushGuestQueue());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void flushGuestQueue();
  });
  window.setInterval(() => {
    if (document.visibilityState === 'visible') void flushGuestQueue();
  }, RETRY_INTERVAL_MS);
  // Late enough that the first paint — and the toast surface a rejection notice
  // needs — exists. The queue is durable, so the delay costs nothing.
  window.setTimeout(() => void flushGuestQueue(), 1_000);
}

export { OnlineRequiredError, failureMessage, isRejection, statusOf } from '@/lib/offline';
export type {
  DeadLetter,
  FlushResult,
  MutationRequest,
  MutationResult,
  OutboxEntry,
  ReplayKeyField,
  Submitted,
} from '@/lib/offline';
